/**
 * Date utility functions.
 *
 * Extracted from auth.js — no global state dependencies.
 *
 * Usage:
 *   import { sortByDate, normalizeDateString } from './helpers/date-utils.js';
 */

import moment from 'moment';
import { dateFormats } from './number-formats.js';

export function sortByDate(array, dateField) {
	if (!array) return null;
	let sortList = [];
	let orderedTimes = [];
	let sortedList = sortList;
	let counterForIndex = 0;
	array.forEach(function (key) {
		sortList.push({ date: new Date(key[dateField]), item: key });
		key.newKey = (counterForIndex++).toString();
		sortList.sort(function (a, b) {
			return b.date - a.date;
		});
	});
	sortedList.forEach(function (key) {
		orderedTimes.push(key.item);
	});
	return orderedTimes;
}

export function dateTimeUTC(timestamp) {
	let date = new Date(timestamp * (timestamp < 20000000000 ? 1000 : 1));
	let dateUTC = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
	return dateUTC;
}

export function timeStampUTC(date, in_ms = false) {
	let timestamp = Date.parse(date);
	let thisDate = new Date(timestamp);
	let timeStampUTC = timestamp - thisDate.getTimezoneOffset() * 60000;
	if (!in_ms) timeStampUTC /= 1000;
	return timeStampUTC;
}

const EXCEL_EPOCH = Date.UTC(1899, 11, 30);
function normalizeDateCore(value, { knownDatestamp = false, preferIsoFirst = false } = {}) {
	if (value === null || value === undefined || value === '') return '';

	// Date object
	if (value instanceof Date) return moment(value).format('YYYY-MM-DD');

	const n = Number(value);
	if (Number.isFinite(n)) {
		// Unix milliseconds: modern-style 13-digit timestamps, positive or negative
		if (Math.abs(n) >= 1000000000000) return moment.utc(n).format('YYYY-MM-DD');

		// Excel serial date: small-ish positive integers that are NOT blocked by knownDatestamp = true
		if (n > 0 && n < 100000 && !knownDatestamp) {
			const serial = Math.floor(n);
			const d = new Date(EXCEL_EPOCH + serial * 86400000);
			return d.toISOString().slice(0, 10);
		}

		// Unix seconds (our standar): usually epoch seconds, positive or negative
		return moment.unix(n).utc().format('YYYY-MM-DD');
	}

	if (typeof value === 'string') {
		let s = value.trim();

		if (preferIsoFirst) {
			const iso = moment(s, moment.ISO_8601, true);
			if (iso.isValid()) return iso.format('YYYY-MM-DD');
		}

		// Remove common human clutter
		s = s
			.replace(/(\d+)(st|nd|rd|th)/gi, '$1') // 1st → 1
			.replace(/\./g, '') // Apr. → Apr
			.replace(/,/g, ''); // Apr 1, 1997

		// PASS 1 — strict formats
		let m = moment(s, dateFormats, true);
		if (m.isValid()) return m.format('YYYY-MM-DD');

		// PASS 2 — relaxed fallback
		m = moment(s);
		if (m.isValid()) return m.format('YYYY-MM-DD');
	}

	return '';
}

export function normalizeDateString(value, knownDatestamp = false) {
	return normalizeDateCore(value, { knownDatestamp });
}

export function normalizeDobDateString(value) {
	return normalizeDateCore(value, { preferIsoFirst: true });
}

// Convert many possible date strings to a Unix timestamp at UTC noon.
// Normalizes to UTC noon so the same calendar date does not drift by browser timezone.
export function dateStringToUnix(dateStr) {
	if (!dateStr) return '';

	const m = moment.utc(dateStr, dateFormats, true);
	if (!m.isValid()) return '';

	return m.hour(12).minute(0).second(0).millisecond(0).unix().toString();
}

// UTC day-boundary helpers — use these for any data-path range comparisons.
// Do NOT use moment(str).startOf/endOf('day') without .utc() in helper/admin code.
export function startOfDayUTC(dateStr) {
	if (!dateStr) return null;
	if (typeof dateStr === 'number') return null; // raw Unix seconds must go through normalizeDateString first
	const m = moment.utc(dateStr);
	if (!m.isValid()) return null;
	return m.startOf('day').unix();
}

export function endOfDayUTC(dateStr) {
	if (!dateStr) return null;
	if (typeof dateStr === 'number') return null; // raw Unix seconds must go through normalizeDateString first
	const m = moment.utc(dateStr);
	if (!m.isValid()) return null;
	return m.endOf('day').unix();
}

// Lightweight date formatter — avoids creating moment objects per row
// Uses UTC methods to prevent timezone shifts (timestamps are stored as UTC dates)
const _dateFormatCache = new Map();
export function fastFormatDate(unixTimestamp) {
	if (_dateFormatCache.has(unixTimestamp)) return _dateFormatCache.get(unixTimestamp);
	const d = new Date(Number(unixTimestamp) * 1000);
	const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
	const result = months[d.getUTCMonth()] + ' ' + d.getUTCDate() + ', ' + d.getUTCFullYear();
	_dateFormatCache.set(unixTimestamp, result);
	return result;
}
